/**
 * O-1A Visa Criteria Definitions
 *
 * The O-1A visa is for individuals with extraordinary ability in sciences, education,
 * business, or athletics. The petitioner must demonstrate sustained national or
 * international acclaim and recognition.
 *
 * Legal Reference: 8 CFR 214.2(o)
 */

import { VisaCriterion } from './types';

/**
 * O-1A visa criteria
 *
 * The beneficiary must meet at least 3 out of 8 criteria (or demonstrate
 * a one-time achievement such as a major internationally recognized award).
 */
export const O1A_CRITERIA: VisaCriterion[] = [
  {
    id: 'o1a_awards',
    name: 'Awards and Recognition',
    description: 'Documentation of receipt of nationally or internationally recognized prizes or awards for excellence in the field of endeavor',
    weight: 9,
    requiredDocuments: [
      'AWARD_CERTIFICATE',
      'GRANT_APPROVAL',
      'COMPETITION_RESULTS'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iii)(A)',
    minimumEvidence: 2
  },
  {
    id: 'o1a_membership',
    name: 'Professional Memberships',
    description: 'Membership in associations in the field which require outstanding achievements of their members, as judged by recognized national or international experts',
    weight: 7,
    requiredDocuments: [
      'MEMBERSHIP_CERTIFICATE'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iii)(B)',
    minimumEvidence: 1
  },
  {
    id: 'o1a_publications',
    name: 'Published Materials',
    description: 'Published material about the individual in professional or major trade publications or other major media, relating to the individual\'s work in the field',
    weight: 8,
    requiredDocuments: [
      'PEER_REVIEWED_ARTICLE',
      'MEDIA_ARTICLE',
      'RESEARCH_PAPER'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iii)(C)',
    minimumEvidence: 3
  },
  {
    id: 'o1a_judging',
    name: 'Judging Others',
    description: 'Evidence of participation, either individually or on a panel, as a judge of the work of others in the same or an allied field of specialization',
    weight: 8,
    requiredDocuments: [
      'JUDGING_INVITATION',
      'PEER_REVIEW_CONFIRMATION'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iii)(D)',
    minimumEvidence: 2
  },
  {
    id: 'o1a_contributions',
    name: 'Original Contributions',
    description: 'Evidence of original scientific, scholarly, artistic, athletic, or business-related contributions of major significance in the field',
    weight: 9,
    requiredDocuments: [
      'RESEARCH_PAPER',
      'PATENT_DOCUMENT',
      'INNOVATION_DESCRIPTION'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iii)(E)',
    minimumEvidence: 2
  },
  {
    id: 'o1a_scholarly_articles',
    name: 'Scholarly Articles',
    description: 'Authorship of scholarly articles in professional or major trade publications or other major media in the field',
    weight: 8,
    requiredDocuments: [
      'PEER_REVIEWED_ARTICLE',
      'CONFERENCE_PROCEEDINGS'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iii)(F)',
    minimumEvidence: 3
  },
  {
    id: 'o1a_critical_role',
    name: 'Critical Role',
    description: 'Evidence that the individual has performed in a leading or critical role for organizations or establishments that have a distinguished reputation',
    weight: 7,
    requiredDocuments: [
      'EMPLOYMENT_CONTRACT',
      'COMPANY_ORGANIZATIONAL_CHART',
      'CRITICAL_ROLE_DOCUMENT'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iii)(G)',
    minimumEvidence: 2
  },
  {
    id: 'o1a_high_salary',
    name: 'High Salary',
    description: 'Evidence that the individual has commanded a high salary or other significantly high remuneration for services in relation to others in the field',
    weight: 6,
    requiredDocuments: [
      'SALARY_STATEMENT',
      'TAX_RETURN',
      'EMPLOYMENT_CONTRACT'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iii)(H)',
    minimumEvidence: 2
  }
];

/**
 * Get O-1A criteria configuration
 */
export const O1A_CONFIG = {
  visaType: 'O-1A',
  criteria: O1A_CRITERIA
};
